import {
  CalendarOutlined,
  CheckOutlined,
  MoreOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Popover,
  Spin,
  Typography,
} from 'antd';
import cn from 'classnames';
import moment from 'moment';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ResponsibleDropdown } from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/components/ResponsibleDropdown';
import { TaskDatePicker } from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/components/TaskDatePicker';
import { TaskTypeDropdown } from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/components/TaskTypeDropdown';
import { LEAD_CARD_PATH } from '@app/constants';

import { useTaskModalForm } from '../hooks/useTaskModalForm';

import type { TaskModalProps } from '../types';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { FC } from 'react';

import '../styles/TaskModal.desktop.scss';

const { TextArea } = Input;
const { Text } = Typography;

const formatCommentTime = (time: string): string =>
  moment(time).format('DD.MM.YYYY HH:mm');

const getCommentAuthor = (creator: {
  NameLast: string | null;
  NameFirst: string | null;
}): string =>
  [creator.NameLast, creator.NameFirst].filter(Boolean).join(' ') || 'Система';

export const TaskModal: FC<TaskModalProps> = ({
  isOpen,
  taskId,
  seed,
  title,
  menuItems,
  onClose,
  onSuccess,
}) => {
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isResponsibleOpen, setIsResponsibleOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);

  const form = useTaskModalForm({ isOpen, taskId, seed });

  const handleSaveClick = async () => {
    const wasCreate = !form.isEditMode;
    const ok = await form.handleSave();
    if (ok) {
      onSuccess?.(wasCreate);
      onClose();
    }
  };

  const handleCompleteClick = async () => {
    const ok = await form.handleToggleComplete();
    if (ok) onSuccess?.();
  };

  const dateOpenChangeHandler = (open: boolean) => {
    if (!open) form.finishDateSelection();
    setIsDateOpen(open);
  };

  const modalTitle = title ?? (form.isEditMode ? 'Задача' : 'Новая задача');
  const client = form.task?.Client ?? null;

  const datePicker = (
    <TaskDatePicker
      startDate={form.startDate}
      endDate={form.endDate}
      preset={form.preset}
      selectionPhase={form.selectionPhase}
      previewStart={form.previewStart}
      previewEnd={form.previewEnd}
      viewMonth={form.viewMonth}
      isAllDay={form.isAllDay}
      startTime={form.startTime}
      endTime={form.endTime}
      onPresetSelect={form.selectPreset}
      onDayClick={form.handleDayClick}
      onDayHover={form.handleDayHover}
      onViewMonthChange={form.setViewMonth}
      onToggleAllDay={form.toggleAllDay}
      onStartTimeChange={form.handleStartTimeChange}
      onEndTimeChange={form.handleEndTimeChange}
      onSetDuration={form.handleSetDuration}
      onClose={() => dateOpenChangeHandler(false)}
    />
  );

  return (
    <Modal
      className="TaskModal"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={460}
      destroyOnHidden
      title={
        <div className="TaskModal__header">
          <span className="TaskModal__title">{modalTitle}</span>
          {menuItems && menuItems.length > 0 && (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                aria-label="Действия"
              />
            </Dropdown>
          )}
        </div>
      }
    >
      <Spin spinning={form.isLoadingTask}>
        <div className="TaskModal__body">
          <div className="TaskModal__chips">
            <Popover
              content={datePicker}
              trigger="click"
              placement="bottomLeft"
              open={isDateOpen}
              onOpenChange={dateOpenChangeHandler}
              arrow={false}
              overlayInnerStyle={{ padding: 0 }}
            >
              <button
                type="button"
                className={cn('TaskModal__chip', {
                  'TaskModal__chip--open': isDateOpen,
                })}
              >
                <CalendarOutlined className="TaskModal__chip-icon" />
                <span>{form.chipLabel}</span>
              </button>
            </Popover>

            <Dropdown
              trigger={['click']}
              open={isResponsibleOpen}
              onOpenChange={setIsResponsibleOpen}
              placement="bottomLeft"
              popupRender={() => (
                <ResponsibleDropdown
                  executors={form.executors}
                  selectedId={form.selectedExecutor?.id ?? null}
                  onSelect={form.selectExecutor}
                  onClose={() => setIsResponsibleOpen(false)}
                />
              )}
            >
              <button
                type="button"
                className={cn('TaskModal__chip', {
                  'TaskModal__chip--muted': form.isResponsibleMuted,
                  'TaskModal__chip--open': isResponsibleOpen,
                })}
              >
                <UserOutlined className="TaskModal__chip-icon" />
                <span>{form.responsibleLabel}</span>
              </button>
            </Dropdown>

            <Dropdown
              trigger={['click']}
              open={isTypeOpen}
              onOpenChange={setIsTypeOpen}
              placement="bottomLeft"
              destroyOnHidden
              popupRender={() => (
                <TaskTypeDropdown
                  taskTypes={form.taskTypes}
                  selectedId={form.selectedTaskType?.Id ?? null}
                  onSelect={form.selectTaskType}
                  onCreate={form.createTaskType}
                  onEdit={form.editTaskType}
                  onDelete={form.deleteTaskType}
                  onClose={() => setIsTypeOpen(false)}
                />
              )}
            >
              <button
                type="button"
                className={cn('TaskModal__chip', {
                  'TaskModal__chip--muted': form.isTaskTypeMuted,
                  'TaskModal__chip--open': isTypeOpen,
                })}
              >
                {form.taskTypeIconId ? (
                  <FontAwesomeIcon
                    className="TaskModal__chip-icon"
                    icon={form.taskTypeIconId as IconProp}
                    style={{ color: form.taskTypeColor || undefined }}
                  />
                ) : null}
                <span>{form.taskTypeLabel}</span>
              </button>
            </Dropdown>
          </div>

          <TextArea
            className="TaskModal__textarea"
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder="Введите текст задачи"
            value={form.description}
            onChange={({ target: { value } }) => form.setDescription(value)}
          />

          {form.isEditMode && form.task && (
            <div className="TaskModal__details">
              {form.task.StatusDescription && (
                <div className="TaskModal__status">
                  <Text type="secondary">Статус: </Text>
                  <Text>{form.task.StatusDescription}</Text>
                </div>
              )}

              {client && (
                <div className="TaskModal__client">
                  <Text type="secondary">Клиент: </Text>
                  {client.LeadId ? (
                    <Link
                      to={`${LEAD_CARD_PATH}?leadId=${client.LeadId}`}
                      target="_blank"
                    >
                      {getCommentAuthor(client)}
                    </Link>
                  ) : (
                    <Text>{getCommentAuthor(client)}</Text>
                  )}
                </div>
              )}

              <div className="TaskModal__comments">
                <Text strong className="TaskModal__comments-title">
                  Комментарии
                </Text>
                {form.comments.length === 0 ? (
                  <Text type="secondary" className="TaskModal__comments-empty">
                    Пока нет комментариев
                  </Text>
                ) : (
                  <div className="TaskModal__comments-list">
                    {form.comments.map((comment) => (
                      <div className="TaskModal__comment" key={comment.Id}>
                        <div className="TaskModal__comment-head">
                          <Text strong className="TaskModal__comment-author">
                            {getCommentAuthor(comment.UserCreator)}
                          </Text>
                          <Text
                            type="secondary"
                            className="TaskModal__comment-time"
                          >
                            {formatCommentTime(comment.TimeCreated)}
                          </Text>
                        </div>
                        <Text className="TaskModal__comment-text">
                          {comment.Text}
                        </Text>
                      </div>
                    ))}
                  </div>
                )}

                <TextArea
                  className="TaskModal__comment-input"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder="Написать комментарий…"
                  value={form.newComment}
                  onChange={({ target: { value } }) =>
                    form.setNewComment(value)
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className="TaskModal__footer">
          {form.isEditMode && (
            <Button
              className="TaskModal__footer-complete"
              icon={form.isCompleted ? <UndoOutlined /> : <CheckOutlined />}
              loading={form.isCompleting}
              onClick={handleCompleteClick}
            >
              {form.isCompleted ? 'Вернуть в работу' : 'Выполнено'}
            </Button>
          )}
          <div className="TaskModal__footer-actions">
            <Button onClick={onClose}>Отменить</Button>
            <Button
              type="primary"
              loading={form.isSaving}
              onClick={handleSaveClick}
            >
              {form.isEditMode ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
